import { LEGAL_VERSIONS } from '../constants/version';
import {
  LegalDocument,
  LegalDocumentType,
  LegalParagraph,
  LegalSection,
} from '../types/legalDocument';

export const termsOfUse: LegalDocument = {
  id: 'terms-of-use',

  title: 'TERMOS DE USO DA NEXORA',

  version: LEGAL_VERSIONS.terms,

  source: 'docs/legal/terms-of-use.md',

  documentType: 'terms' as LegalDocumentType,

  lastUpdated: 'Julho de 2026',

  effectiveDate: 'Julho de 2026',

  sections: [
    {
      id: 'commitment',

      title: 'Compromisso da Nexora',

      paragraphs: [
        {
          id: 'commitment-1',
          text: 'A Nexora acredita que tecnologia, segurança e transparência devem caminhar juntas.',
        },

        {
          id: 'commitment-2',
          text: 'Estes Termos de Uso estabelecem as condições para utilização da plataforma, definindo direitos, responsabilidades e regras aplicáveis à relação entre a Nexora e seus usuários.',
        },

        {
          id: 'commitment-3',
          text: 'Ao criar uma conta ou utilizar qualquer recurso da plataforma, o usuário declara ter lido, compreendido e concordado com estes Termos.',
        },
      ] satisfies LegalParagraph[],
    },

    {
      id: 'about-nexora',

      title: '1. Sobre a Nexora',

      paragraphs: [
        {
          id: 'about-nexora-1',
          text: 'A Nexora é uma plataforma SaaS destinada à organização, gerenciamento e centralização de conhecimento, prompts, recursos de inteligência artificial e conteúdos relacionados.',
        },

        {
          id: 'about-nexora-2',
          text: 'A plataforma disponibiliza ferramentas para administração de usuários, gerenciamento de conteúdo, controle de acesso e organização de informações, podendo incorporar novos recursos conforme sua evolução.',
        },
      ] satisfies LegalParagraph[],
    },

    {
      id: 'eligibility',

      title: '2. Elegibilidade',

      paragraphs: [
        {
          id: 'eligibility-1',
          text: 'Para utilizar a Nexora, o usuário deverá:',
        },

        {
          id: 'eligibility-2',
          text: '* possuir capacidade legal para celebrar contratos;',
        },

        {
          id: 'eligibility-3',
          text: '* fornecer informações verdadeiras durante o cadastro;',
        },

        {
          id: 'eligibility-4',
          text: '* aceitar os documentos oficiais da plataforma;',
        },

        {
          id: 'eligibility-5',
          text: '* respeitar todas as políticas vigentes.',
        },

        {
          id: 'eligibility-6',
          text: 'A Nexora poderá solicitar informações adicionais sempre que necessário para validação da conta.',
        },
      ] satisfies LegalParagraph[],
    },

    {
      id: 'registration-and-account',

      title: '3. Cadastro e Conta',

      paragraphs: [
        {
          id: 'registration-and-account-1',
          text: 'O cadastro na plataforma é individual.',
        },

        {
          id: 'registration-and-account-2',
          text: 'Cada usuário é responsável pelas informações fornecidas e pela utilização de sua conta.',
        },

        {
          id: 'registration-and-account-3',
          text: 'A criação de uma conta não garante, por si só, acesso imediato aos recursos da plataforma.',
        },
      ] satisfies LegalParagraph[],
    },

    {
      id: 'approval-process',

      title: '4. Processo de Aprovação',

      paragraphs: [
        {
          id: 'approval-process-1',
          text: 'A Nexora utiliza um processo administrativo de aprovação de contas.',
        },

        {
          id: 'approval-process-2',
          text: 'Após o cadastro, a conta poderá permanecer em análise até que seja liberada pela administração.',
        },

        {
          id: 'approval-process-3',
          text: 'Durante esse período, determinados recursos poderão permanecer indisponíveis.',
        },

        {
          id: 'approval-process-4',
          text: 'A aprovação ou não da conta é realizada conforme critérios internos da plataforma.',
        },
      ] satisfies LegalParagraph[],
    },

    {
      id: 'subscriptions-and-plans',

      title: '5. Assinaturas e Planos',

      paragraphs: [
        {
          id: 'subscriptions-and-plans-1',
          text: 'Alguns recursos da Nexora poderão depender da contratação de um plano ou assinatura.',
        },

        {
          id: 'subscriptions-and-plans-2',
          text: 'As condições de utilização, renovação, cancelamento e expiração são descritas na Política de Assinaturas da plataforma.',
        },
      ] satisfies LegalParagraph[],
    },

    {
      id: 'access-suspension',

      title: '6. Suspensão de Acesso',

      paragraphs: [
        {
          id: 'access-suspension-1',
          text: 'O acesso poderá ser suspenso temporariamente em situações como:',
        },

        {
          id: 'access-suspension-2',
          text: '* expiração da assinatura;',
        },

        {
          id: 'access-suspension-3',
          text: '* necessidade de análise administrativa;',
        },

        {
          id: 'access-suspension-4',
          text: '* investigação de atividades incompatíveis com as políticas da plataforma;',
        },

        {
          id: 'access-suspension-5',
          text: '* outras hipóteses previstas nos documentos oficiais da Nexora.',
        },

        {
          id: 'access-suspension-6',
          text: 'Durante a suspensão, o acesso aos recursos poderá permanecer indisponível até a regularização da situação.',
        },
      ] satisfies LegalParagraph[],
    },

    {
      id: 'account-block',

      title: '7. Bloqueio da Conta',

      paragraphs: [
        {
          id: 'account-block-1',
          text: 'A Nexora poderá bloquear permanentemente uma conta quando identificar situações que comprometam a segurança da plataforma, violem estes Termos ou descumpram o Código de Conduta.',
        },

        {
          id: 'account-block-2',
          text: 'Sempre que possível, a decisão será fundamentada em critérios técnicos e administrativos.',
        },
      ] satisfies LegalParagraph[],
    },

    {
      id: 'security',

      title: '8. Segurança',

      paragraphs: [
        {
          id: 'security-1',
          text: 'A Nexora adota medidas técnicas e administrativas para proteger os dados armazenados em sua infraestrutura.',
        },

        {
          id: 'security-2',
          text: 'Entretanto, o usuário também é responsável por manter a confidencialidade de suas credenciais de acesso e utilizar a plataforma de forma segura.',
        },
      ] satisfies LegalParagraph[],
    },

    {
      id: 'intellectual-property',

      title: '9. Propriedade Intelectual',

      paragraphs: [
        {
          id: 'intellectual-property-1',
          text: 'A identidade visual, marca, logotipos, software, interfaces, documentação e demais elementos pertencentes à Nexora são protegidos pela legislação aplicável.',
        },

        {
          id: 'intellectual-property-2',
          text: 'Nenhum destes elementos poderá ser reproduzido, distribuído ou explorado comercialmente sem autorização expressa da Nexora.',
        },
      ] satisfies LegalParagraph[],
    },

    {
      id: 'platform-availability',

      title: '10. Disponibilidade da Plataforma',

      paragraphs: [
        {
          id: 'platform-availability-1',
          text: 'A Nexora busca manter seus serviços disponíveis continuamente.',
        },

        {
          id: 'platform-availability-2',
          text: 'No entanto, poderão ocorrer interrupções decorrentes de:',
        },

        {
          id: 'platform-availability-3',
          text: '* manutenção programada;',
        },

        {
          id: 'platform-availability-4',
          text: '* atualizações;',
        },

        {
          id: 'platform-availability-5',
          text: '* falhas técnicas;',
        },

        {
          id: 'platform-availability-6',
          text: '* fatores externos;',
        },

        {
          id: 'platform-availability-7',
          text: '* eventos fora do controle da plataforma.',
        },

        {
          id: 'platform-availability-8',
          text: 'Essas situações não caracterizam descumprimento destes Termos.',
        },
      ] satisfies LegalParagraph[],
    },

    {
      id: 'platform-changes',

      title: '11. Alterações da Plataforma',

      paragraphs: [
        {
          id: 'platform-changes-1',
          text: 'A Nexora poderá adicionar, remover, modificar ou descontinuar funcionalidades com o objetivo de melhorar seus serviços.',
        },

        {
          id: 'platform-changes-2',
          text: 'Sempre que possível, alterações relevantes serão comunicadas aos usuários.',
        },
      ] satisfies LegalParagraph[],
    },

    {
      id: 'terms-update',

      title: '12. Atualização dos Termos',

      paragraphs: [
        {
          id: 'terms-update-1',
          text: 'Estes Termos poderão ser revisados periodicamente.',
        },

        {
          id: 'terms-update-2',
          text: 'Sempre que uma nova versão entrar em vigor, a Nexora poderá solicitar um novo aceite antes da continuidade da utilização da plataforma.',
        },
      ] satisfies LegalParagraph[],
    },

    {
      id: 'contact',

      title: '13. Contato',

      paragraphs: [
        {
          id: 'contact-1',
          text: 'Em caso de dúvidas relacionadas a estes Termos de Uso, o usuário poderá utilizar os canais oficiais disponibilizados pela Nexora.',
        },
      ] satisfies LegalParagraph[],
    },

    {
      id: 'final-provisions',

      title: 'Disposições Finais',

      paragraphs: [
        {
          id: 'final-provisions-1',
          text: 'Ao utilizar a Nexora, o usuário declara estar ciente destes Termos e compromete-se a respeitar todas as políticas, documentos e diretrizes oficiais da plataforma.',
        },

        {
          id: 'final-provisions-2',
          text: 'A Nexora permanece comprometida em oferecer um ambiente seguro, organizado, transparente e preparado para evoluir continuamente em benefício de seus usuários.',
        },
      ] satisfies LegalParagraph[],
    },
  ] satisfies LegalSection[],
};
